import React from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useTourBookings } from '@/hooks/useTourBookings';

interface PayPalCheckoutProps {
  booking: any;
  amount: string;
  currency?: string;
  onSuccess?: (details: any) => void;
  onError?: (error: any) => void;
}

export const PayPalCheckout: React.FC<PayPalCheckoutProps> = ({
  booking,
  amount,
  currency = 'USD',
  onSuccess,
  onError
}) => {
  const [{ isResolved, isPending }] = usePayPalScriptReducer();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { createTourBooking } = useTourBookings();

  const createOrder = async () => {
    try {
      const currentUrl = window.location.origin;
      // Determine if this is a tour, transfer, or rental booking based on booking data
      const isTourBooking = booking.tour_name || booking.selectedDate;
      const isRentalBooking = booking.vehicle_name || booking.vehicle_id;
      
      let returnUrl, cancelUrl;
      if (isRentalBooking) {
        returnUrl = `${currentUrl}/rental-booking-confirmation?booking=${booking.booking_number}`;
        cancelUrl = `${currentUrl}/car-rental`;
      } else if (isTourBooking) {
        returnUrl = `${currentUrl}/paypal-success-tour?booking_number=${booking.booking_number}`;
        cancelUrl = `${currentUrl}/paypal-cancel-tour?booking_number=${booking.booking_number}`;
      } else {
        returnUrl = `${currentUrl}/paypal-success?booking_number=${booking.booking_number}`;
        cancelUrl = `${currentUrl}/paypal-cancel?booking_number=${booking.booking_number}`;
      }

      // For tour bookings, we need to create the booking first if it doesn't exist
      if (isTourBooking && booking.booking_number.startsWith('TEMP-')) {
        // Create the actual tour booking using the hook
        const tourBookingData = {
          tour_name: booking.tourName || booking.tour_name,
          customer_name: booking.customer_name,
          email: booking.email,
          phone: booking.phone,
          pickup_point: booking.pickup_point,
          booking_date: booking.selectedDate || booking.booking_date,
          tour_schedule: booking.selectedTime || booking.tour_schedule,
          adults: booking.adults,
          total_price: parseFloat(amount),
          payment_method: 'paypal',
          payment_status: 'pending' as const,
          booking_status: 'pending' as const,
        };

        const newBooking = await createTourBooking(tourBookingData);
        
        if (!newBooking) {
          throw new Error('Failed to create tour booking');
        }

        // Update booking reference with real booking number
        booking.booking_number = newBooking.booking_number;
      }

      // Create PayPal order using our edge function (it handles credential fetching internally)
      const orderFunction = isRentalBooking ? 'create-paypal-order-rental' : 'create-paypal-order';
      const { data, error } = await supabase.functions.invoke(orderFunction, {
        body: {
          amount: amount,
          currency: currency,
          booking_number: booking.booking_number,
          return_url: returnUrl,
          cancel_url: cancelUrl
        }
      });

      if (error || !data?.success) {
        throw new Error(data?.error || 'Failed to create PayPal order');
      }

      return data.order_id;
    } catch (error: any) {
      console.error('Error creating PayPal order:', error);
      if (onError) onError(error);
      toast({
        title: "PayPal Error",
        description: error.message || "Failed to create PayPal order",
        variant: "destructive",
      });
      throw error;
    }
  };

  const onApprove = async (data: any) => {
    try {
      // Determine verification function based on booking type
      const isTourBooking = booking.tour_name || booking.selectedDate;
      const isRentalBooking = booking.vehicle_name || booking.vehicle_id;
      const verifyFunction = isRentalBooking ? 'verify-paypal-payment-rental' : 'verify-paypal-payment';

      // Verify the payment using our edge function
      const { data: verifyData, error } = await supabase.functions.invoke(verifyFunction, {
        body: {
          order_id: data.orderID,
          booking_number: booking.booking_number
        }
      });

      if (error || !verifyData?.success) {
        const errorMessage = verifyData?.error || 'Payment verification failed';
        const errorType = verifyData?.error_type || 'SYSTEM_ERROR';
        const retryAllowed = verifyData?.retry_allowed || false;
        
        throw new Error(errorMessage);
      }

      toast({
        title: "Payment Successful!",
        description: "Your booking has been confirmed.",
      });

      if (onSuccess) {
        onSuccess({
          ...verifyData,
          booking_number: booking.booking_number
        });
      } else {
        // Navigate to appropriate confirmation page
        const isRentalBooking = booking.vehicle_name || booking.vehicle_id;
        let confirmationPath = '/booking-confirmation';
        if (isRentalBooking) {
          confirmationPath = `/rental-booking-confirmation?booking=${booking.booking_number}`;
        } else if (isTourBooking) {
          confirmationPath = '/tour-booking-confirmation';
        }
        
        navigate(confirmationPath, { 
          state: { 
            booking: booking,
            paymentData: verifyData 
          } 
        });
      }
    } catch (error: any) {
      console.error('Error verifying payment:', error);
      if (onError) onError(error);
      
      // Provide specific error messages based on PayPal error types
      let title = "Payment Failed";
      let description = error.message || "Please contact support";
      
      if (error.message.includes('INSTRUMENT_DECLINED')) {
        title = "Card Declined";
        description = "Your payment method was declined. Please try a different card or use your PayPal balance.";
      } else if (error.message.includes('INSUFFICIENT_FUNDS')) {
        title = "Insufficient Funds";
        description = "Insufficient funds or card limit exceeded. Please try a different payment method.";
      } else if (error.message.includes('PAYPAL_ERROR')) {
        title = "Payment Processing Error";
        description = error.message.replace('PAYPAL_ERROR: ', '');
      }
      
      toast({
        title,
        description,
        variant: "destructive",
      });
    }
  };

  const onCancel = () => {
    toast({
      title: "Payment Cancelled",
      description: "You can try again when ready.",
      variant: "default",
    });
  };

  const onPayPalError = (error: any) => {
    console.error('PayPal error:', error);
    if (onError) onError(error);
    toast({
      title: "PayPal Error",
      description: "There was an error processing your payment",
      variant: "destructive",
    });
  };

  if (!isResolved) {
    return (
      <div className="w-full p-6 bg-muted/50 rounded-lg animate-pulse">
        <div className="h-12 bg-muted rounded mb-4"></div>
        <div className="text-center text-muted-foreground">Loading PayPal...</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <PayPalButtons
        style={{
          layout: "vertical",
          color: "blue",
          shape: "rect",
          label: "paypal",
          height: 50
        }}
        createOrder={createOrder}
        onApprove={onApprove}
        onCancel={onCancel}
        onError={onPayPalError}
        disabled={isPending}
      />
    </div>
  );
};